"use client"

import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useRef, useState, useTransition } from "react"

import { convertToLocale } from "@lib/util/money"
import {
  COLLECTION_SORT_OPTIONS,
  sizeToParam,
} from "@lib/util/collection-facets"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { SortOptions } from "@modules/store/components/refinement-list/sort-products"
import type { Crumb } from "./CollectionTemplate"

type Props = {
  breadcrumbs: Crumb[]
  availableSizes: string[]
  priceMin: number
  priceMax: number
  currencyCode: string
  selectedSizes: string[] // URL slugs
  selectedMin?: number
  selectedMax?: number
  sortBy: SortOptions
  totalFiltered: number
}

type PanelKey = "size" | "price" | "order"

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    aria-hidden="true"
    className={`transition-transform ${open ? "rotate-180" : ""}`}
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
)

// Sliders / filter glyph for the mobile toggle.
const FilterGlyph = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M3 6h13M3 12h9M3 18h5" />
    <circle cx="19" cy="6" r="2" />
    <circle cx="15" cy="12" r="2" />
    <circle cx="11" cy="18" r="2" />
  </svg>
)

export default function CollectionFilterBar({
  breadcrumbs,
  availableSizes,
  priceMin,
  priceMax,
  currencyCode,
  selectedSizes,
  selectedMin,
  selectedMax,
  sortBy,
  totalFiltered,
}: Props) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const barRef = useRef<HTMLDivElement>(null)
  const [isPending, startTransition] = useTransition()

  const [open, setOpen] = useState<PanelKey | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)

  const hasPriceRange = priceMax > priceMin
  const activeSlugs = new Set(selectedSizes)
  const priceActive = selectedMin !== undefined || selectedMax !== undefined
  const activeFilterCount = selectedSizes.length + (priceActive ? 1 : 0)

  const [minInput, setMinInput] = useState(
    selectedMin !== undefined ? String(selectedMin) : ""
  )
  const [maxInput, setMaxInput] = useState(
    selectedMax !== undefined ? String(selectedMax) : ""
  )
  useEffect(() => {
    setMinInput(selectedMin !== undefined ? String(selectedMin) : "")
    setMaxInput(selectedMax !== undefined ? String(selectedMax) : "")
  }, [selectedMin, selectedMax])

  // Close desktop dropdowns on outside click / ESC.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node))
        setOpen(null)
    }
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(null)
    document.addEventListener("mousedown", onDown)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDown)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  // Lock body scroll + ESC-to-close while the mobile sheet is open.
  useEffect(() => {
    if (!sheetOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setSheetOpen(false)
    document.addEventListener("keydown", onKey)
    return () => {
      document.body.style.overflow = prev
      document.removeEventListener("keydown", onKey)
    }
  }, [sheetOpen])

  const commit = useCallback(
    (updates: Record<string, string | null>) => {
      const params = new URLSearchParams(searchParams.toString())
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === "") params.delete(key)
        else params.set(key, value)
      }
      params.delete("page")
      const qs = params.toString()
      startTransition(() => {
        router.push(qs ? `${pathname}?${qs}` : pathname, { scroll: false })
      })
    },
    [pathname, router, searchParams]
  )

  const toggleSize = (rawSize: string) => {
    const slug = sizeToParam(rawSize)
    const next = activeSlugs.has(slug)
      ? selectedSizes.filter((s) => s !== slug)
      : [...selectedSizes, slug]
    commit({ size: next.join(",") || null })
  }

  const applyPrice = () => {
    const min = minInput.trim()
    const max = maxInput.trim()
    commit({
      minPrice: min !== "" && Number(min) > priceMin ? min : null,
      maxPrice: max !== "" && Number(max) < priceMax ? max : null,
    })
    setOpen(null)
  }

  const clearPrice = () => {
    commit({ minPrice: null, maxPrice: null })
    setOpen(null)
  }

  const clearAll = () => {
    commit({ size: null, minPrice: null, maxPrice: null })
  }

  const setSort = (value: SortOptions) => {
    commit({ sortBy: value })
    setOpen(null)
  }

  const sortLabel =
    COLLECTION_SORT_OPTIONS.find((o) => o.value === sortBy)?.label ||
    "Latest Arrivals"
  const priceLabel = (a: number) =>
    convertToLocale({ amount: a, currency_code: currencyCode })

  const triggerCls = (active: boolean, isOpen: boolean) =>
    `inline-flex items-center gap-1.5 rounded-sm border px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.08em] transition-colors xsmall:text-[12px] ${
      active || isOpen
        ? "border-alif-green text-alif-green"
        : "border-alif-rule text-alif-ink hover:border-alif-green"
    }`

  const panelCls =
    "absolute right-0 top-[calc(100%+8px)] z-40 min-w-[220px] rounded-sm border border-alif-rule bg-white p-4 shadow-md"

  // ── Shared size-chip + sort-row builders (reused desktop + mobile sheet) ──
  const SizeChips = ({ size = "sm" }: { size?: "sm" | "lg" }) => (
    <div className="flex flex-wrap gap-2">
      {availableSizes.map((s) => {
        const active = activeSlugs.has(sizeToParam(s))
        return (
          <button
            key={s}
            type="button"
            aria-pressed={active}
            onClick={() => toggleSize(s)}
            className={`rounded-sm border px-2.5 font-medium transition-colors ${
              size === "lg"
                ? "min-h-[44px] min-w-[48px] text-[13px]"
                : "min-h-[36px] min-w-[40px] text-[12px]"
            } ${
              active
                ? "border-alif-green bg-alif-green text-white"
                : "border-alif-rule text-alif-ink hover:border-alif-green hover:text-alif-green"
            }`}
          >
            {s}
          </button>
        )
      })}
    </div>
  )

  return (
    <div
      ref={barRef}
      className="relative mb-8 flex items-center justify-between gap-3 border-b border-alif-rule pb-4 small:gap-4"
      role="toolbar"
      aria-label="Breadcrumb, filter and sort"
      aria-busy={isPending}
    >
      {/* Pending indicator — the page re-renders server-side on each change. */}
      {isPending && (
        <span
          aria-hidden="true"
          className="absolute inset-x-0 -bottom-px h-0.5 animate-pulse bg-alif-green"
        />
      )}

      {/* ── Breadcrumb (left) — stays on one line, truncates last crumb ── */}
      <nav
        aria-label="Breadcrumb"
        className="alif-label flex min-w-0 flex-1 items-center gap-2 overflow-hidden text-alif-muted"
      >
        {breadcrumbs.map((crumb, i) => {
          const isLast = i === breadcrumbs.length - 1
          return (
            <span
              key={`${crumb.name}-${i}`}
              className={`flex items-center gap-2 ${
                isLast ? "min-w-0" : "shrink-0"
              }`}
            >
              {crumb.href && !isLast ? (
                <LocalizedClientLink
                  href={crumb.href}
                  className="whitespace-nowrap transition-colors hover:text-alif-green"
                >
                  {crumb.name}
                </LocalizedClientLink>
              ) : (
                <span className={isLast ? "truncate text-alif-ink" : "whitespace-nowrap"}>
                  {crumb.name}
                </span>
              )}
              {!isLast && (
                <span aria-hidden="true" className="text-alif-rule">
                  /
                </span>
              )}
            </span>
          )
        })}
      </nav>

      {/* ── Desktop inline dropdowns (right) ── */}
      <div className="hidden shrink-0 items-center gap-2 small:flex small:gap-3">
        {/* Size */}
        {availableSizes.length > 0 && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setOpen((p) => (p === "size" ? null : "size"))}
              className={triggerCls(selectedSizes.length > 0, open === "size")}
              aria-haspopup="true"
              aria-expanded={open === "size"}
            >
              Size
              {selectedSizes.length > 0 && (
                <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-alif-green px-1 text-[9px] text-white">
                  {selectedSizes.length}
                </span>
              )}
              <Chevron open={open === "size"} />
            </button>
            {open === "size" && (
              <div className={panelCls}>
                <div className="max-w-[240px]">
                  <SizeChips />
                </div>
                {selectedSizes.length > 0 && (
                  <button
                    type="button"
                    onClick={() => commit({ size: null })}
                    className="mt-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-alif-muted underline underline-offset-2 hover:text-alif-green"
                  >
                    Clear
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Price */}
        {hasPriceRange && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setOpen((p) => (p === "price" ? null : "price"))}
              className={triggerCls(priceActive, open === "price")}
              aria-haspopup="true"
              aria-expanded={open === "price"}
            >
              Price
              {priceActive && (
                <span
                  className="h-1.5 w-1.5 rounded-full bg-alif-green"
                  aria-hidden="true"
                />
              )}
              <Chevron open={open === "price"} />
            </button>
            {open === "price" && (
              <div className={panelCls}>
                <p className="mb-3 text-[12px] text-alif-muted">
                  {priceLabel(priceMin)} – {priceLabel(priceMax)}
                </p>
                <div className="flex items-end gap-2">
                  <label className="flex-1">
                    <span className="mb-1 block text-[11px] uppercase tracking-[0.08em] text-alif-muted">
                      Min
                    </span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={priceMin}
                      max={priceMax}
                      value={minInput}
                      placeholder={String(priceMin)}
                      onChange={(e) => setMinInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && applyPrice()}
                      className="h-10 w-full rounded-sm border border-alif-rule bg-white px-3 text-[14px] text-alif-ink outline-none focus:border-alif-green"
                    />
                  </label>
                  <label className="flex-1">
                    <span className="mb-1 block text-[11px] uppercase tracking-[0.08em] text-alif-muted">
                      Max
                    </span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={priceMin}
                      max={priceMax}
                      value={maxInput}
                      placeholder={String(priceMax)}
                      onChange={(e) => setMaxInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && applyPrice()}
                      className="h-10 w-full rounded-sm border border-alif-rule bg-white px-3 text-[14px] text-alif-ink outline-none focus:border-alif-green"
                    />
                  </label>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  {priceActive ? (
                    <button
                      type="button"
                      onClick={clearPrice}
                      className="text-[11px] font-semibold uppercase tracking-[0.08em] text-alif-muted underline underline-offset-2 hover:text-alif-green"
                    >
                      Clear
                    </button>
                  ) : (
                    <span />
                  )}
                  <button
                    type="button"
                    onClick={applyPrice}
                    className="rounded-sm bg-alif-green px-5 py-2 text-[12px] font-semibold uppercase tracking-[0.08em] text-white transition-colors hover:bg-alif-green-deep"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Order / sort */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((p) => (p === "order" ? null : "order"))}
            className={triggerCls(false, open === "order")}
            aria-haspopup="true"
            aria-expanded={open === "order"}
          >
            <span className="text-alif-muted">Order</span>
            <span className="text-alif-ink">{sortLabel}</span>
            <Chevron open={open === "order"} />
          </button>
          {open === "order" && (
            <div
              className={`${panelCls} min-w-[200px]`}
              role="listbox"
              aria-label="Order"
            >
              {COLLECTION_SORT_OPTIONS.map((opt) => {
                const active = sortBy === opt.value
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => setSort(opt.value)}
                    className={`block w-full rounded-sm px-3 py-2.5 text-left text-[13px] transition-colors hover:bg-alif-green-soft ${
                      active ? "font-semibold text-alif-green" : "text-alif-ink"
                    }`}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Mobile toggle (right) — opens the bottom sheet ── */}
      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className="inline-flex shrink-0 items-center gap-2 rounded-sm border border-alif-rule px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-alif-ink small:hidden"
        aria-haspopup="dialog"
        aria-expanded={sheetOpen}
      >
        <FilterGlyph />
        Filter
        {activeFilterCount > 0 && (
          <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-alif-green px-1 text-[9px] text-white">
            {activeFilterCount}
          </span>
        )}
      </button>

      {/* ── Mobile bottom sheet ── */}
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[60] bg-alif-ink/40 transition-opacity duration-200 small:hidden ${
          sheetOpen
            ? "opacity-100"
            : "pointer-events-none opacity-0"
        }`}
        onClick={() => setSheetOpen(false)}
        aria-hidden="true"
      />
      {/* Sheet */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Filter and sort"
        aria-hidden={!sheetOpen}
        className={`fixed inset-x-0 bottom-0 z-[60] flex max-h-[88vh] flex-col rounded-t-2xl bg-white shadow-[0_-8px_30px_rgba(26,22,18,0.18)] transition-transform duration-300 ease-out small:hidden ${
          sheetOpen
            ? "translate-y-0"
            : "pointer-events-none translate-y-full"
        }`}
      >
        {/* Grab handle */}
        <div className="flex justify-center pt-3">
          <span className="h-1 w-10 rounded-full bg-alif-rule" aria-hidden="true" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pb-4 pt-3">
          <button
            type="button"
            onClick={() => setSheetOpen(false)}
            className="-ml-1 flex h-9 w-9 items-center justify-center text-alif-ink"
            aria-label="Close filters"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              aria-hidden="true"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
          <h2 className="font-display text-[20px] text-alif-ink">
            Filter &amp; Sort
          </h2>
          <button
            type="button"
            onClick={clearAll}
            disabled={activeFilterCount === 0}
            className="text-[12px] text-alif-muted transition-colors enabled:hover:text-alif-green disabled:opacity-40"
          >
            Clear All
          </button>
        </div>

        <div className="h-px bg-alif-rule" />

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {/* Sort by */}
          <fieldset>
            <legend className="alif-label mb-3 text-alif-ink">Sort by</legend>
            <div className="space-y-1">
              {COLLECTION_SORT_OPTIONS.map((opt) => {
                const active = sortBy === opt.value
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setSort(opt.value)}
                    className="flex w-full items-center gap-3 py-2.5 text-left"
                  >
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                        active ? "border-alif-green" : "border-alif-rule"
                      }`}
                      aria-hidden="true"
                    >
                      {active && (
                        <span className="h-2.5 w-2.5 rounded-full bg-alif-green" />
                      )}
                    </span>
                    <span
                      className={`text-[15px] ${
                        active ? "text-alif-ink" : "text-alif-muted"
                      }`}
                    >
                      {opt.label}
                    </span>
                  </button>
                )
              })}
            </div>
          </fieldset>

          {/* Filter by */}
          {(availableSizes.length > 0 || hasPriceRange) && (
            <p className="alif-label mb-3 mt-7 text-alif-ink">Filter by</p>
          )}

          {/* Size */}
          {availableSizes.length > 0 && (
            <div className="border-t border-alif-rule py-5">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-[15px] text-alif-ink">Size</span>
                {selectedSizes.length > 0 && (
                  <button
                    type="button"
                    onClick={() => commit({ size: null })}
                    className="text-[12px] text-alif-muted underline underline-offset-2 hover:text-alif-green"
                  >
                    Clear
                  </button>
                )}
              </div>
              <SizeChips size="lg" />
            </div>
          )}

          {/* Price */}
          {hasPriceRange && (
            <div className="border-t border-alif-rule py-5">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-[15px] text-alif-ink">Price</span>
                {priceActive && (
                  <button
                    type="button"
                    onClick={clearPrice}
                    className="text-[12px] text-alif-muted underline underline-offset-2 hover:text-alif-green"
                  >
                    Clear
                  </button>
                )}
              </div>
              <p className="mb-3 text-[13px] text-alif-muted">
                {priceLabel(priceMin)} – {priceLabel(priceMax)}
              </p>
              <div className="flex items-end gap-3">
                <label className="flex-1">
                  <span className="mb-1 block text-[11px] uppercase tracking-[0.08em] text-alif-muted">
                    Min
                  </span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={priceMin}
                    max={priceMax}
                    value={minInput}
                    placeholder={String(priceMin)}
                    onChange={(e) => setMinInput(e.target.value)}
                    className="h-12 w-full rounded-sm border border-alif-rule bg-white px-3 text-[15px] text-alif-ink outline-none focus:border-alif-green"
                  />
                </label>
                <label className="flex-1">
                  <span className="mb-1 block text-[11px] uppercase tracking-[0.08em] text-alif-muted">
                    Max
                  </span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={priceMin}
                    max={priceMax}
                    value={maxInput}
                    placeholder={String(priceMax)}
                    onChange={(e) => setMaxInput(e.target.value)}
                    className="h-12 w-full rounded-sm border border-alif-rule bg-white px-3 text-[15px] text-alif-ink outline-none focus:border-alif-green"
                  />
                </label>
                <button
                  type="button"
                  onClick={applyPrice}
                  className="h-12 shrink-0 rounded-sm bg-alif-green px-5 text-[12px] font-semibold uppercase tracking-[0.08em] text-white transition-colors hover:bg-alif-green-deep"
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer CTA */}
        <div className="border-t border-alif-rule px-5 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={() => setSheetOpen(false)}
            className="flex h-12 w-full items-center justify-center rounded-sm bg-alif-ink text-[13px] font-semibold uppercase tracking-[0.1em] text-white transition-colors hover:bg-alif-green"
          >
            Show {totalFiltered} Result{totalFiltered !== 1 ? "s" : ""}
          </button>
        </div>
      </div>
    </div>
  )
}
